import type { LoopFormState } from "@/hooks/use-loop-form";
import { VENUES } from "@/config/contracts";
import { USDC } from "@/config/tokens";
import { formatNumber, formatPercent } from "@/lib/format";

type Props = {
  readonly form: LoopFormState;
};

type Row = {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
};

export const MarketReadout = ({ form }: Props) => {
  const { borrowApr, availableLiquidity, venue, leverage } = form;
  const isMorpho = venue === "morpho";
  const lender = VENUES[venue];

  // Net carry of the levered position on the user's principal. The CADC
  // collateral earns nothing on the lending venue, so the only leg is the
  // borrow cost, amplified by the borrowed multiple (L − 1). Negative = a cost.
  const leverageApr =
    borrowApr == null ? null : -borrowApr * Math.max(0, leverage - 1);

  const rows: Row[] = [
    {
      label: "Available to borrow",
      value:
        availableLiquidity == null
          ? "—"
          : `${formatNumber(availableLiquidity, 0)} ${USDC.symbol}`,
      hint: lender.longLabel,
    },
    {
      label: "Borrow APR",
      value: borrowApr == null ? "—" : formatPercent(borrowApr, 2),
    },
    {
      label: "Leverage APR",
      value: leverageApr == null ? "—" : formatPercent(leverageApr, 2),
      hint: `net carry at ${formatNumber(leverage, 2)}×`,
    },
  ];

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h3>Market</h3>
        <span
          className="font-mono text-[10px] tracking-[0.16em] uppercase"
          style={{ color: "var(--ink-4)" }}
        >
          {isMorpho ? "Live · Morpho" : "Live · Ajna"}
        </span>
      </div>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li
            key={r.label}
            className="flex items-baseline justify-between gap-6 pb-3"
            style={{
              borderBottom:
                i === rows.length - 1 ? "none" : "1px dashed var(--line-1)",
            }}
          >
            <span
              className="font-sans text-[12px] tracking-[0.04em]"
              style={{ color: "var(--ink-3)" }}
            >
              {r.label}
            </span>
            <span className="text-right">
              <span
                className="font-mono tabular text-[14px]"
                style={{ color: "var(--ink-1)", fontWeight: 400 }}
              >
                {r.value}
              </span>
              {r.hint && (
                <span
                  className="block font-sans text-[10px] tracking-[0.08em] uppercase mt-1"
                  style={{ color: "var(--ink-4)" }}
                >
                  {r.hint}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
